<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Rastreamento de Palavras-chave
        </PageTitle>
        <PageDescription>
          Monitore o desempenho das suas palavras-chave e otimize suas campanhas.
        </PageDescription>
      </div>
    </div>

    <!-- Summary Cards -->
    <!-- <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
      <div v-for="(card, index) in summaryCards" :key="index" class="bg-white/5 rounded-xl overflow-hidden">
        <div class="p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-zinc-400">{{ card.title }}</p>
              <h3 class="text-2xl font-semibold text-white mt-1">{{ card.value }}</h3>
            </div>
            <div :class="`bg-${card.color}-500/10 p-3 rounded-full`">
              <component :is="card.icon" class="h-5 w-5" :class="`text-${card.color}-500`" />
            </div>
          </div>
        </div>
      </div>
    </div> -->

    <!-- Keywords Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Palavras-chave registradas</h2>
        <div class="flex items-center gap-4">
          <!-- <div class="relative">
            <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 h-4 w-4" />
            <Input 
              v-model="searchTerm" 
              placeholder="Buscar palavras-chave..." 
              class="pl-10 bg-white/5 border-none text-white focus-visible:ring-emerald-500 w-64"
            />
          </div> -->
          <!-- <Select v-model="filterStatus">
            <SelectTrigger class="bg-white/5 border-none text-white w-40">
              <SelectValue placeholder="Filtrar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="high">Alta conversão</SelectItem>
              <SelectItem value="medium">Média conversão</SelectItem>
              <SelectItem value="low">Baixa conversão</SelectItem>
            </SelectContent>
          </Select> -->
        </div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="header in tableHeaders"
              :key="header.key"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon 
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="keywords">
            <TableFallback 
              v-if="keywords.data.length === 0" 
              :colspan="tableHeaders.length" 
              :icon="KeyIcon" 
              title="Nenhuma palavra-chave encontrada" 
              :description="`Você deve adicionar a sua palavra-chave após ?start= no link do seu bot para começar`" 
            />
            <TableRow
              v-for="keyword in keywords.data"
              :key="keyword.id"
            >
              <TableCell>
                <div class="bg-[#2c3b4b4f] p-2 px-8 rounded inline-flex py-2.5 px-3 text-center relative">
                  <div class="text-sm font-medium text-white">{{ keyword.content }}</div>
                  <!-- <ArrowUpRight class="size-3 absolute right-1 top-1 text-white" /> -->
                </div>
              </TableCell>
              <TableCell>
                <div class="text-sm text-white">{{ keyword.leadsCount }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm text-white">{{ keyword.paidOrdersCount }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm text-white">{{ (keyword.ordersCount ?? 0) - (keyword.paidOrdersCount ?? 0) }}</div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2">
                  <div class="bg-purple-500/10 py-1.5 px-3 rounded inline-flex items-center justify-between gap-2 relative min-w-16">
                    <div class="text-sm font-medium text-white">{{ keyword.upsellCount || 0 }}</div>
                    <ArrowUpRight class="size-3 text-purple-400" />
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2">
                  <div class="bg-amber-500/10 py-1.5 px-3 rounded inline-flex items-center justify-between gap-2 relative min-w-16">
                    <div class="text-sm font-medium text-white">{{ keyword.downsellCount || 0 }}</div>
                    <ArrowDownRight class="size-3 text-amber-400" />
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex flex-col gap-1.5">
                  <div 
                    class="py-1.5 px-3 rounded inline-flex items-center justify-between gap-2 relative min-w-16"
                    :class="['bg-emerald-500/20']"
                  >
                    <div 
                      class="text-sm font-medium"
                      :class="['text-emerald-300']"
                    >
                      {{
                        keyword.leadsCount && keyword.paidOrdersCount
                          ? ((keyword.paidOrdersCount / keyword.leadsCount) * 100).toFixed(1) + '%'
                          : '0%'
                      }}
                    </div>
                  </div>
                  <div class="w-full h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
                    <div 
                      class="h-full rounded-full transition-all duration-500 ease-out"
                      :class="`bg-emerald-600`"
                      :style="`width: ${keyword.leadsCount && keyword.paidOrdersCount ? (keyword.paidOrdersCount / keyword.leadsCount) * 100 : 0}%`"
                    ></div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton v-else :columns="tableHeaders.length" />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="keywords">
        <template v-if="keywords.data.length > 0">
          <MobileCard v-for="keyword in keywords.data" :key="keyword.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Palavra-chave</span>
                  <div class="flex items-center gap-2">
                    <div class="bg-[#2c3b4b4f] p-1 px-3 rounded inline-flex text-center relative">
                        <span class="text-white font-bold text-lg">{{ keyword.content }}</span>
                    </div>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #content>
                <div class="grid grid-cols-2 gap-4">
                    <MobileDataLabel label="Total de Leads">
                       {{ keyword.leadsCount }}
                    </MobileDataLabel>
                    <MobileDataLabel label="Vendas">
                       {{ keyword.paidOrdersCount }}
                    </MobileDataLabel>
                    <MobileDataLabel label="Pix abandonados">
                       {{ (keyword.ordersCount ?? 0) - (keyword.paidOrdersCount ?? 0) }}
                    </MobileDataLabel>
                    <MobileDataLabel label="Upsells">
                       {{ keyword.upsellCount || 0 }}
                    </MobileDataLabel>
                    <MobileDataLabel label="Downsells">
                       {{ keyword.downsellCount || 0 }}
                    </MobileDataLabel>
                </div>
                
                <div class="flex items-center justify-between pt-2 mt-2 border-t border-white/5">
                   <div class="flex flex-col w-full">
                     <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Taxa de Conversão</span>
                     <div class="flex items-center gap-2">
                        <div class="text-xl font-bold text-emerald-400">
                           {{ keyword.leadsCount && keyword.paidOrdersCount ? ((keyword.paidOrdersCount / keyword.leadsCount) * 100).toFixed(1) + '%' : '0%' }}
                        </div>
                        <div class="w-full h-1.5 bg-gray-700/50 rounded-full overflow-hidden flex-1 max-w-[100px]">
                            <div 
                              class="h-full rounded-full bg-emerald-500 transition-all duration-500 ease-out"
                              :style="`width: ${keyword.leadsCount && keyword.paidOrdersCount ? (keyword.paidOrdersCount / keyword.leadsCount) * 100 : 0}%`"
                            ></div>
                        </div>
                     </div>
                   </div>
                </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <KeyIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhuma palavra-chave encontrada
        </div>
      </div>

      <!-- Pagination -->
      <Pagination 
        @update:page="page => reloadAndGetAll({ page, perPage })"
        v-if="keywords?.pagination" 
        :data="keywords?.pagination" 
        v-model:per-page="perPage" 
      />
    </TableContainer>
  </main>
  
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import {
  ArrowsUpDownIcon,
  KeyIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import { Input } from '@/components/ui/input'
import { 
  ArrowUpRight, 
  Search, 
  ArrowUpRight as ArrowUp,
  ArrowDownRight,
  CreditCard, 
} from 'lucide-vue-next'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { useToast } from '@/components/ui/toast'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useLeadStartParameters } from '@/composables/useLeadStartParameters'
import { LeadStartParameter } from '@/interfaces/leads/LeadStartParam'

interface GetAllKeywordsParams {
  page?: string
  perPage?: string
}

// Table Configuration
const tableHeaders = [
  { key: 'keyword', label: 'Palavra-chave', sortable: false },
  { key: 'totalLeads', label: 'Total de Leads', sortable: true },
  { key: 'buyers', label: 'Vendas', sortable: true },
  { key: 'abandonedCheckouts', label: 'Pix abandonados', sortable: true },
  { key: 'upsellers', label: 'Upsells', sortable: true },
  { key: 'downsellers', label: 'Downsells', sortable: true },
  { key: 'conversionRate', label: 'Taxa de Conversão', sortable: true },
]

// State
const keywords = ref<IPagination<LeadStartParameter>>()
const { toast } = useToast()

// Dialog and Form
const { getAll } = useLeadStartParameters()
const perPage = ref('10')
const searchTerm = ref('')

const sortBy = (key: string) => {
  // Implement sorting logic
  // This would typically sort the keywords.value.data array based on the key
}

const reloadAndGetAll = async (params: GetAllKeywordsParams) => {
  keywords.value = await getAll()
}

const getConversionColor = (rate: number) => {
  if (rate >= 20) return 'emerald';
  if (rate >= 10) return 'amber';
  return 'red';
};

watch(() => perPage.value, () => {
  reloadAndGetAll({ perPage: perPage.value })
})

onMounted(async () => {
  keywords.value = await getAll()
})
</script>